import { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { archive, studies as studiesApi,
         type ArchiveDocumentListItem, type ArchiveBackfillResult } from '@/api/endpoints';
import type { StudyType } from '@/types';
import { Icon } from '@/components/Icon';
import { Pagination, usePaginated } from '@/components/Pagination';
import { SearchableSelect } from '@/components/SearchableSelect';

/**
 * Admin → Archiwum (RAG): listowanie wszystkich dokumentów w bazie wektorowej.
 * Filtrowanie po typie opracowania i statusie indeksacji. Akcje: pobierz, reindeks, hard-delete.
 * Globalny backfill typów na podstawie powiązań z opracowaniami (jednorazowa migracja).
 */
export function AdminArchive() {
  const [items, setItems] = useState<ArchiveDocumentListItem[] | null>(null);
  const [studyTypes, setStudyTypes] = useState<StudyType[]>([]);
  const [filterType, setFilterType] = useState<string>('');
  const [filterIndexed, setFilterIndexed] = useState<'all' | 'yes' | 'no'>('all');
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [backfilling, setBackfilling] = useState(false);
  const [reindexingAll, setReindexingAll] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [graceDays, setGraceDays] = useState<number>(7);

  const reload = () => {
    archive.list({
      studyType: filterType || null,
      indexed: filterIndexed === 'all' ? null : filterIndexed === 'yes',
    }).then(setItems).catch(e => setErr(String(e)));
  };

  useEffect(() => {
    studiesApi.types().then(setStudyTypes).catch(() => setStudyTypes([]));
    archive.softDeleteConfig()
      .then(c => setGraceDays(c.graceDays))
      .catch(() => { /* zostaje default 7 */ });
  }, []);

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, filterIndexed]);

  const filtered = useMemo(() => {
    if (!items) return [];
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(d =>
      (d.name?.toLowerCase().includes(q) ?? false) ||
      (d.intakeName?.toLowerCase().includes(q) ?? false) ||
      (d.clientName?.toLowerCase().includes(q) ?? false));
  }, [items, search]);

  const { pagedItems, page, pageSize, setPage, setPageSize, total: pageTotal } = usePaginated(filtered, { storageKey: 'admin-archive', defaultPageSize: 50 });

  const stats = useMemo(() => {
    if (!items) return null;
    const total = items.length;
    const indexed = items.filter(d => d.indexed).length;
    const noType = items.filter(d => !d.studyType).length;
    const totalSize = items.reduce((s, d) => s + (d.sizeBytes ?? 0), 0);
    return { total, indexed, noType, totalSize };
  }, [items]);

  const groupedByType = useMemo(() => {
    if (!items) return {};
    const out: Record<string, number> = { __null__: 0 };
    for (const d of items) {
      const k = d.studyType ?? '__null__';
      out[k] = (out[k] ?? 0) + 1;
    }
    return out;
  }, [items]);

  async function onRequestDelete(id: number, name: string) {
    const dni = graceDays === 1 ? 'dzień' : graceDays < 5 ? 'dni' : 'dni';
    if (!confirm(`Oznaczyć "${name}" do usunięcia? Plik zostanie skasowany trwale za ${graceDays} ${dni}; do tego czasu możesz cofnąć usunięcie.`)) return;
    setBusyId(id);
    setErr(null); setInfo(null);
    try {
      const res = await archive.requestDelete(id);  // backend bierze domyślny z konfiguracji
      if (res.alreadyPending) {
        setInfo(`"${name}" był już oznaczony do usunięcia. Termin: ${res.deleteAfter}.`);
      } else {
        setInfo(`Oznaczono "${res.name}" do usunięcia. Trwałe skasowanie: ${res.deleteAfter}. Możesz cofnąć w międzyczasie.`);
      }
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  async function onCancelDelete(id: number, name: string) {
    setBusyId(id);
    setErr(null); setInfo(null);
    try {
      await archive.cancelDelete(id);
      setInfo(`Cofnięto usunięcie "${name}" — plik znowu aktywny.`);
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  async function onForceDelete(id: number, name: string) {
    if (!confirm(`USUNIĘCIE NATYCHMIASTOWE: "${name}"\n\nTo nie jest soft-delete — plik znika z DB i MinIO bez 7-dniowego okna.\nKontynuować?`)) return;
    setBusyId(id);
    setErr(null); setInfo(null);
    try {
      const res = await archive.forceDelete(id);
      setInfo(`Hard-delete (natychmiast) "${res.name}" — ${res.fragmentsRemoved} fragm., plik MinIO: ${res.fileRemoved ? 'OK' : 'pominięty'}.`);
      setItems(prev => (prev ?? []).filter(d => d.id !== id));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  async function onToggleRag(item: ArchiveDocumentListItem) {
    const newVal = !item.ragEnabled;
    if (!newVal && !confirm(`Wyłączyć indeksację RAG dla "${item.name}"?\nPlik zostanie w archiwum, ale AI-drafty go nie znajdą.`)) return;
    setBusyId(item.id);
    setErr(null); setInfo(null);
    try {
      await archive.setRagEnabled(item.id, newVal);
      setInfo(newVal
        ? `Włączono indeksację RAG dla "${item.name}".${item.fragmentsCount === 0 ? ' Reindex w toku.' : ''}`
        : `Wyłączono indeksację RAG dla "${item.name}". AI-drafty go pominą.`);
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  async function onReindex(id: number) {
    setBusyId(id);
    setErr(null); setInfo(null);
    try {
      const res = await archive.reindex(id);
      if (res.success) {
        setInfo(`Re-indeks OK: ${res.fragmentsCreated} fragm. (${res.extractedChars} znaków, ${res.extractionMethod}).`);
        reload();
      } else {
        setErr(`Re-indeks fail: ${res.error}`);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  async function onReindexAll() {
    if (!items || items.length === 0) return;
    if (!confirm(`Re-indeksować wszystkie ${items.length} dokumentów? Może potrwać kilka minut.`)) return;
    setReindexingAll(true);
    setErr(null); setInfo(null);
    try {
      const sum = await archive.reindexAll();
      setInfo(`Re-indeks bulk: ${sum.success}/${sum.total} OK · ${sum.totalFragments} fragmentów total · ${sum.failed} błędów.`);
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setReindexingAll(false);
    }
  }

  async function onBackfill() {
    if (!confirm('Uzupełnij typy opracowań dla istniejących dokumentów na podstawie ich powiązań z opracowaniami w portalu?')) return;
    setBackfilling(true);
    setErr(null); setInfo(null);
    try {
      const res: ArchiveBackfillResult = await archive.backfillStudyTypes();
      setInfo(`Backfill: uzupełniono ${res.updated} dokumentów. Pozostało bez typu: ${res.totalNullAfter}.`);
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBackfilling(false);
    }
  }

  return (
    <div className="max-w-[1400px]">
      <header className="flex items-end justify-between mb-5">
        <div>
          <h1>Archiwum RAG</h1>
          <div className="text-ink-400 text-[13px]">
            Wszystkie dokumenty w bazie wektorowej. Każde opracowanie po zakończeniu (K12) automatycznie
            klasyfikuje swoje pliki i indeksuje brakujące. Tu możesz przeglądać/usuwać ręcznie.
            <span className="block mt-1 text-[11px]">
              Soft-delete: <strong>{graceDays} {graceDays === 1 ? 'dzień' : 'dni'}</strong> grace period —
              zmień w <NavLink to="/admin/config" className="text-water-700 hover:underline">Konfiguracji systemu</NavLink>
              {' '}(klucz <code className="font-mono text-[10px]">archive.soft-delete.grace-days</code>).
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={onBackfill} disabled={backfilling}
                  className="btn btn-secondary btn-sm"
                  title="Uzupełnia studyType na dokumentach bez typu na podstawie powiązań z opracowaniami">
            {backfilling
              ? <><svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25"/>
                  <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                </svg> Uzupełnianie…</>
              : <><Icon name="check" className="w-3.5 h-3.5"/> Uzupełnij typy</>}
          </button>
          <button onClick={onReindexAll} disabled={reindexingAll}
                  className="btn btn-secondary btn-sm">
            {reindexingAll
              ? <><svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25"/>
                  <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                </svg> Re-indeksuję…</>
              : <><Icon name="sparkles" className="w-3.5 h-3.5"/> Re-indeksuj wszystko</>}
          </button>
        </div>
      </header>

      {err && <div className="text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-3 text-[12px]">Błąd: {err}</div>}
      {info && <div className="text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2 mb-3 text-[12px]">{info}</div>}

      {stats && (
        <div className="grid grid-cols-4 gap-3 mb-4">
          <div className="kpi"><div className="absolute inset-y-0 left-0 w-[3px] bg-water-500"/>
            <div className="kpi-label">Wszystkie</div><div className="kpi-value">{stats.total}</div></div>
          <div className="kpi"><div className="absolute inset-y-0 left-0 w-[3px] bg-emerald-500"/>
            <div className="kpi-label">Zindeksowane</div><div className="kpi-value">{stats.indexed}</div></div>
          <div className="kpi"><div className="absolute inset-y-0 left-0 w-[3px] bg-amber-500"/>
            <div className="kpi-label">Bez typu</div><div className="kpi-value">{stats.noType}</div></div>
          <div className="kpi"><div className="absolute inset-y-0 left-0 w-[3px] bg-ink-400"/>
            <div className="kpi-label">Rozmiar łącznie</div><div className="kpi-value text-[18px]">
              {stats.totalSize > 0 ? `${(stats.totalSize / 1024 / 1024).toFixed(1)} MB` : '—'}
            </div></div>
        </div>
      )}

      <div className="card mb-4">
        <div className="card-head">
          <div className="card-title">Filtry</div>
        </div>
        <div className="card-body flex flex-wrap items-end gap-3">
          <label className="field flex-1 min-w-[200px]">
            <span className="field-label">Typ opracowania</span>
            <SearchableSelect value={filterType} onChange={setFilterType}>
              <option value="">— wszystkie ({items?.length ?? 0}) —</option>
              {studyTypes.map(t => (
                <option key={t.id} value={t.id}>
                  {t.abbreviation} {t.code} — {t.name} ({groupedByType[t.id] ?? 0})
                </option>
              ))}
              <option value="__null__" disabled>(bez typu: {groupedByType['__null__'] ?? 0})</option>
            </SearchableSelect>
          </label>
          <label className="field min-w-[160px]">
            <span className="field-label">Status indeksacji</span>
            <SearchableSelect value={filterIndexed}
                    onChange={v => setFilterIndexed(v as 'all' | 'yes' | 'no')}>
              <option value="all">wszystkie</option>
              <option value="yes">zindeksowane</option>
              <option value="no">bez indeksu</option>
            </SearchableSelect>
          </label>
          <label className="field flex-1 min-w-[200px]">
            <span className="field-label">Szukaj (nazwa / ujęcie / klient)</span>
            <input value={search} onChange={e => setSearch(e.target.value)} className="input"
                   placeholder="np. dziennik wierceń, Śrem, AquaSrem"/>
          </label>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <div className="card-title">Dokumenty ({filtered.length}{filtered.length !== items?.length && items ? ` z ${items.length}` : ''})</div>
        </div>
        <div className="card-body !p-0">
          {!items && <div className="p-6 text-center text-ink-400">Ładowanie…</div>}
          {items && filtered.length === 0 && (
            <div className="p-6 text-center text-ink-400 text-[12px]">
              {items.length === 0 ? 'Archiwum jest puste.' : 'Brak dokumentów pasujących do filtrów.'}
            </div>
          )}
          {filtered.length > 0 && (
            <table className="table-hw">
              <thead>
                <tr>
                  <th>Nazwa</th>
                  <th>Typ opr.</th>
                  <th>Ujęcie / Klient</th>
                  <th className="text-right">Rozmiar</th>
                  <th className="text-right">Fragm.</th>
                  <th>Indeks</th>
                  <th>RAG (AI)</th>
                  <th>Dodany</th>
                  <th className="text-right">Akcje</th>
                </tr>
              </thead>
              <tbody>
                {pagedItems.map(d => {
                  const busy = busyId === d.id;
                  const pending = d.deletionRequestedAt != null;
                  const daysLeft = d.deleteAfter
                    ? Math.max(0, Math.ceil((new Date(d.deleteAfter).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
                    : null;
                  return (
                    <tr key={d.id} className={(busy ? 'opacity-50' : '') + (pending ? ' bg-red-50/40' : '')}>
                      <td>
                        <div className={'font-semibold text-[13px] max-w-[320px] truncate '
                              + (pending ? 'text-red-700 line-through' : 'text-ink-900')}
                             title={d.name}>{d.name}</div>
                        <div className="font-mono text-[10px] text-ink-400">#{d.id}
                          {d.contentSha256 && (
                            <> · <span title={d.contentSha256}>{d.contentSha256.slice(0, 12)}…</span></>
                          )}
                        </div>
                        {pending && (
                          <div className="text-[10px] text-red-700 font-semibold mt-0.5"
                               title={`Oznaczone przez ${d.deletionRequestedByName ?? '?'} dnia ${d.deletionRequestedAt}`}>
                            ⏱ usunięcie: {daysLeft != null && daysLeft > 0
                              ? `za ${daysLeft} ${daysLeft === 1 ? 'dzień' : 'dni'}`
                              : 'dzisiaj/przeterminowane'}
                            {' '}({d.deleteAfter})
                          </div>
                        )}
                      </td>
                      <td>
                        {d.studyType
                          ? <span className="pill pill-earth" title={d.studyTypeName ?? ''}>
                              {d.studyTypeAbbr ?? d.studyType}
                            </span>
                          : <span className="text-amber-600 text-[11px] italic">brak</span>}
                      </td>
                      <td className="text-[12px]">
                        {d.intakeName && <div>{d.intakeName}</div>}
                        {d.clientName && <div className="text-ink-500 text-[11px]">{d.clientName}</div>}
                        {!d.intakeName && !d.clientName && <span className="text-ink-400 italic">—</span>}
                      </td>
                      <td className="text-right font-mono tabular-nums text-[12px]">
                        {d.sizeBytes != null
                          ? d.sizeBytes < 1024 ? `${d.sizeBytes} B`
                          : d.sizeBytes < 1024 * 1024 ? `${(d.sizeBytes / 1024).toFixed(1)} KB`
                          : `${(d.sizeBytes / 1024 / 1024).toFixed(1)} MB`
                          : '—'}
                      </td>
                      <td className="text-right font-mono tabular-nums">
                        {d.fragmentsCount > 0
                          ? <span className="text-emerald-700">{d.fragmentsCount}</span>
                          : <span className="text-ink-400">0</span>}
                      </td>
                      <td>
                        {d.indexed
                          ? <span className="pill pill-green">OK</span>
                          : <span className="pill pill-amber">brak</span>}
                      </td>
                      <td>
                        <button onClick={() => onToggleRag(d)} disabled={busy}
                                className="hover:underline"
                                title={d.ragEnabled
                                  ? 'Plik dostępny dla AI. Kliknij aby wyłączyć indeksację RAG.'
                                  : 'Plik POMINIĘTY przez AI. Kliknij aby włączyć indeksację RAG.'}>
                          {d.ragEnabled
                            ? <span className="pill pill-green">tak</span>
                            : <span className="pill pill-amber">nie</span>}
                        </button>
                      </td>
                      <td className="text-[11px] text-ink-500 font-mono">
                        {d.createdAt ? new Date(d.createdAt).toLocaleDateString('pl-PL') : '—'}
                      </td>
                      <td className="text-right whitespace-nowrap">
                        <a href={`/api/archive/documents/${d.id}/download`}
                           target="_blank" rel="noreferrer"
                           className="text-water-700 hover:text-water-900 px-1" title="Pobierz">
                          <Icon name="download" className="w-3.5 h-3.5 inline"/>
                        </a>
                        <button onClick={() => onReindex(d.id)} disabled={busy}
                                className="text-ink-500 hover:text-water-700 px-1"
                                title="Re-indeksuj (chunking + embedding)">
                          <Icon name="sparkles" className="w-3.5 h-3.5"/>
                        </button>
                        {d.deletionRequestedAt ? (
                          <button onClick={() => onCancelDelete(d.id, d.name)} disabled={busy}
                                  className="text-emerald-600 hover:text-emerald-800 px-1"
                                  title="Cofnij usunięcie">
                            <Icon name="check" className="w-3.5 h-3.5"/>
                          </button>
                        ) : (
                          <button onClick={() => onRequestDelete(d.id, d.name)} disabled={busy}
                                  className="text-ink-400 hover:text-amber-600 px-1"
                                  title={`Oznacz do usunięcia (${graceDays} ${graceDays === 1 ? 'dzień' : 'dni'} grace period)`}>
                            <Icon name="x" className="w-3.5 h-3.5"/>
                          </button>
                        )}
                        <button onClick={() => onForceDelete(d.id, d.name)} disabled={busy}
                                className="text-ink-300 hover:text-red-700 px-1"
                                title="Usuń trwale natychmiast (admin escape, omija 7-dniowe okno)">
                          <span className="text-[10px] font-bold">⚡</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {filtered.length > 0 && (
            <div className="px-3 border-t border-ink-100">
              <Pagination total={pageTotal} page={page} pageSize={pageSize}
                          onPageChange={setPage} onPageSizeChange={setPageSize}/>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
